import type { Command } from 'commander';
import { validateAggregate } from '@wport/core';
import { createAggregate, type OrchestrationResult } from '../../../lib/resume-orchestrator';
import type { PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, printTable, sanitizeForTerminal } from '../../../lib/output';

interface CreateFlags {
	file: string;
}

/**
 * `resumes create` 核心：本地 `validateAggregate` 先驗（有 issue → exit 2，不發任何請求，
 * 訊息呈現同 `resumes validate` 慣例）→ 通過才進 `createAggregate` 編排（建殼 → rename →
 * 依序逐節寫入）。結果先印出（json/table），再視 `allOk` 決定是否丟 CliError 觸發非 0 exit ——
 * 同 `jobs batch` 慣例：腳本先讀到完整結果，才看到非 0 exit code。
 */
export async function runResumesCreate(ctx: ResolvedContext, filePath: string): Promise<void> {
	const input = readJsonObject(filePath, { timeoutMs: ctx.timeoutMs });
	const issues = validateAggregate(input);
	if (issues.length > 0) {
		if (ctx.format === 'json') {
			printJson({ valid: false, issues });
		} else {
			for (const issue of issues) {
				process.stderr.write(`${sanitizeForTerminal(issue.path)}: ${sanitizeForTerminal(issue.message)}\n`);
			}
		}
		throw new CliError(`${issues.length} validation issue(s) found — fix locally before creating (no request sent)`, ExitCode.InvalidArgument);
	}

	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const result = await createAggregate(opts, input);
	printOrchestrationResult(ctx, result);

	if (!result.allOk) {
		const failed = result.reports.filter((r) => !r.ok).map((r) => r.section);
		throw new CliError(
			`Resume ${result.enc_id} was created but ${failed.length} section(s) failed to write: ${failed.join(', ')}. ` +
				`Run \`wport personal resumes update ${result.enc_id} --section <name> --file <file>\` to retry the failed section(s).`,
			ExitCode.ServerClientError
		);
	}
}

/**
 * create/update 共用的結果呈現：json 印 `{enc_id, reports, all_ok}`；table 先印 enc_id 前綴行
 * （create 情境下這是使用者唯一得知新 enc_id 的地方），再每節一行 ✓/✗ + error。update.ts 各自
 * 複製一份（檔案小而聚焦，同 list.ts formatDate 慣例），不額外拉一個共用模組。
 */
function printOrchestrationResult(ctx: ResolvedContext, result: OrchestrationResult): void {
	if (ctx.format === 'json') {
		printJson({ enc_id: result.enc_id, reports: result.reports, all_ok: result.allOk });
		return;
	}
	process.stdout.write(`Resume: ${sanitizeForTerminal(result.enc_id ?? '')}\n`);
	printTable(
		result.reports,
		[
			{ header: 'SECTION', value: (r) => r.section, maxWidth: 20 },
			{ header: 'STATUS', value: (r) => (r.ok ? '✓' : '✗') },
			{ header: 'ERROR', value: (r) => r.error ?? '', maxWidth: 60 },
		],
		ctx.color
	);
}

export function registerPersonalResumesCreate(parent: Command): void {
	parent
		.command('create')
		.description('Create a resume from a JSON aggregate file (validates locally first; use "-" for stdin)')
		.requiredOption('--file <path>', 'path to a JSON resume aggregate, or "-" for stdin')
		.action(async (flags: CreateFlags, command: Command) => {
			await runResumesCreate(resolveContext(command), flags.file);
		});
}
